/**
 * Anpassningsbart tilläggsavtal till lokalhyresavtal — clean_tillagg_lokal v1.
 *
 * A blank frame (ADR-0061): parties and Avtalskoppling are inherited from the
 * parent lokalhyresavtal, the body is the user's own custom sections, and
 * signing is optional ("Inkludera signering"). No form number.
 */
import { applyDefaults, COMPOSITION_SCHEMA, COMPOSITION_VERSION } from '@hyranai/regta-document-ir';
import { CLEAN_TILLAGG_LOKAL_BLOCKS, DEFAULT_DOCUMENT_TITLE } from '../clean_tillagg_lokal/blocks.js';
import { CUSTOM_SECTIONS_KEY, declaredSectionInstances, slotText } from '../common/index.js';
const K = 'clean_tillagg_lokal';
const SIGNING_INCLUDED = { not: { key: 'include_signing', equals: false } };
/** The seeded "Innehåll" section (legacy `defaultCustomSections`), with a stable id. */
const SEEDED_SECTIONS = [{ id: 'innehall', title: 'Innehåll', subSections: [] }];
const SLOTS = CLEAN_TILLAGG_LOKAL_BLOCKS.flatMap((b) => b.slots);
export const cleanTillaggLokalV1 = (values = {}) => {
    const merged = applyDefaults({ fields: SLOTS }, { [CUSTOM_SECTIONS_KEY]: SEEDED_SECTIONS, ...values });
    return {
        schema: COMPOSITION_SCHEMA,
        version: COMPOSITION_VERSION,
        documentKey: 'clean_tillagg_lokal',
        documentVersion: 'v1',
        title: slotText(merged.document_title) ?? DEFAULT_DOCUMENT_TITLE,
        locale: 'sv-SE',
        meta: {
            category: 'Lokalhyra',
            numberStyle: 'paragraph',
            shortTitle: 'Anpassningsbart tilläggsavtal (lokal)',
            description: 'Ett tomt tilläggsavtal till ett lokalhyresavtal. Parter och fastighet ' +
                'ärvs från avtalet; resten skriver du själv som egna sektioner. Välj om ' +
                'tillägget ska signeras eller endast exporteras.',
            status: 'active',
            signingMode: 'digital',
            signingOptional: true,
            signingPlacementAnchors: 'per-party-label',
            titleFromValueKey: 'document_title',
            connectedTo: ['lokalhyra'],
            connectedOnly: true,
            inheritFromParent: {
                parties: 'by-role-ordinal',
                property: true,
                values: [{ key: 'contract_number', fromParentKey: 'contract_number' }],
            },
            defaultCustomSections: [{ title: 'Innehåll' }],
            partySlots: [
                { id: 'lessor', label: 'Hyresvärd', role: 'lessor', ordinal: 0, required: true, signs: true },
                { id: 'lessee', label: 'Hyresgäst', role: 'lessee', ordinal: 0, required: true, signs: true },
            ],
            propertySlots: [{ id: 'property', label: 'Fastighet', multi: false, required: true }],
        },
        values: merged,
        blocks: [
            { instanceId: 'letterhead', blockRef: `${K}.letterhead@v1`, slot: 'letterhead', input: {} },
            { instanceId: 'footer', blockRef: 'common.page-footer@v1', slot: 'footer', input: {} },
            ...declaredSectionInstances([
                { ref: 'hyresvard', blockRef: `${K}.hyresvard@v1` },
                { ref: 'hyresgast', blockRef: `${K}.hyresgast@v1`, besidePrevious: true },
                { ref: 'avtalskoppling', blockRef: `${K}.avtalskoppling@v1` },
                {
                    ref: 'underskrift',
                    blockRef: `${K}.underskrifter@v1`,
                    includeWhen: SIGNING_INCLUDED,
                    input: {
                        ref: 'underskrift',
                        title: 'Underskrifter',
                        parties: [
                            { label: 'Hyresvärd', role: 'lessor', ordinal: 0 },
                            { label: 'Hyresgäst', role: 'lessee', ordinal: 0 },
                        ],
                    },
                },
            ], { customBlockRef: `${K}.custom-sections@v1` }),
        ],
    };
};
//# sourceMappingURL=clean_tillagg_lokal.js.map